import React from "react";
import {
  Box, Button, Dialog, IconButton, Stack, TextField, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ColorPicker from "../../../../Components/ColorPicker/ColorPicker";
import IconPicker from "../../../../Components/IconPicker/IconPicker";
import { LegacyTokens, TypeScale } from "../../../../theme/b13Tokens";
import { B13 } from "../../../../theme/b13Tokens";
import type { EditPhaseValue } from "./EditPhaseModal";

// Batch 7C §7C.3 — Add new phase modal. Same anatomy as EditPhaseModal
// minus the destructive REMOVE FROM WORKFLOW action (there is nothing
// to remove — this row does not exist yet). Label opens empty and
// focused; default blue swatch; default icon (null → PhaseCard falls
// back to the generic phase icon via resolvePhaseIcon).

export type AddPhaseModalProps = {
  open: boolean;
  onCancel: () => void;
  onSave: (next: EditPhaseValue) => void;
};

export default function AddPhaseModal({ open, onCancel, onSave }: AddPhaseModalProps) {
  const [draft, setDraft] = React.useState<EditPhaseValue>({
    phaseName: "", colour: LegacyTokens.blue, icon: null,
  });
  const labelRef = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    if (open) {
      setDraft({ phaseName: "", colour: LegacyTokens.blue, icon: null });
      setTimeout(() => { labelRef.current?.focus(); }, 0);
    }
  }, [open]);

  const canSave = draft.phaseName.trim().length > 0;

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth={false}
      PaperProps={{
        sx: { width: 520, maxWidth: "calc(100vw - 32px)", borderRadius: 3, overflow: "hidden" },
      }}
    >
      <Box
        sx={{
          px: 3, py: 2,
          display: "flex", alignItems: "center", gap: 1,
          borderBottom: `1px solid ${LegacyTokens.line}`,
        }}
      >
        <Typography sx={{ fontSize: 20, fontWeight: 600, color: LegacyTokens.ink, flexGrow: 1 }}>
          Add new phase
        </Typography>
        <IconButton onClick={onCancel} aria-label="Close" sx={{ color: LegacyTokens.ink3 }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ px: 3, py: 3 }}>
        <Stack direction="row" spacing={2} alignItems="flex-end">
          <Box>
            <Typography sx={{ ...TypeScale.fieldLabel, mb: 0.75, display: "block" }}>Colour</Typography>
            <ColorPicker
              value={draft.colour}
              onChange={(hex) => setDraft((d) => ({ ...d, colour: hex }))}
              size={56}
              ariaLabel="Phase colour"
            />
          </Box>
          <Box>
            <Typography sx={{ ...TypeScale.fieldLabel, mb: 0.75, display: "block" }}>Icon</Typography>
            <IconPicker value={draft.icon} onChange={(name) => setDraft((d) => ({ ...d, icon: name }))} />
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Typography sx={{ ...TypeScale.fieldLabel, mb: 0.5, display: "block" }}>Label</Typography>
            <TextField
              fullWidth variant="standard"
              placeholder="Phase name"
              value={draft.phaseName}
              inputRef={labelRef}
              onChange={(e) => setDraft((d) => ({ ...d, phaseName: e.target.value }))}
              onKeyDown={(e) => {
                if (e.key === "Enter" && canSave) { e.preventDefault(); onSave(draft); }
              }}
              InputProps={{
                sx: {
                  fontSize: 18, fontWeight: 500, color: LegacyTokens.ink,
                  "&:after": { borderBottomColor: B13.primary },
                },
              }}
            />
          </Box>
        </Stack>
      </Box>

      <Box
        sx={{
          px: 3, py: 1.5,
          borderTop: `1px solid ${LegacyTokens.line}`,
          display: "flex", alignItems: "center", justifyContent: "flex-end",
          backgroundColor: LegacyTokens.surface,
        }}
      >
        <Button
          onClick={onCancel}
          sx={{
            textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600,
            color: "#D32F2F",
            "&:hover": { backgroundColor: "rgba(211, 47, 47, 0.06)" },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => onSave(draft)}
          disabled={!canSave}
          sx={{
            textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700,
            color: "#1A73E8",
            "&:hover": { backgroundColor: "rgba(26, 115, 232, 0.06)" },
            "&.Mui-disabled": { color: "rgba(0,0,0,0.26)" },
          }}
        >
          Save
        </Button>
      </Box>
    </Dialog>
  );
}
